<template>
  <div class="container">
    <a-card>
      <div slot="title">
        <h5 style="float: left;margin-top: 0.5rem;">Freightmate Trackers Overview</h5>
      </div>
      <a-col :span="24">
        <a-row>
          <a-alert v-if="alertVisible" type="error" :message="alertMessage" show-icon closable/>
          <br/>
        </a-row>

        <!-- On Site Trackers Summary -->
        <a-row>
          <p class="sub-title">Unlinked Freightmates On Site: {{ onSiteSummary }}</p>
          <a-popover placement="right">
            <template slot="content">
              <span>
                These are the Freightmates trackers that are at your default pickup location.<br>
                It would be great if you could reuse, recycle or return these Freightmates<br>
                so they can assist you or other Freighthub customers.
              </span>
            </template>
            <i class="fa fa-info-circle" aria-hidden="true"></i>
          </a-popover>

          <!-- On Site Trackers Table -->
          <freight-mates-table :columns="onSiteColumns" :data="onSiteTrackers" :loading="isLoadingOnSite" :type="'on-site'"/>
          <br/>
        </a-row>

        <!-- Off Site Trackers Summary -->
        <a-row>
          <p class="sub-title">Unlinked Freightmates Off Site: {{ offSiteSummary }}</p>
          <a-popover placement="right">
            <template slot="content">
              <span>
                These are the Freightmates trackers that have been <br>
                sent to your customers. It would be great if you could <br>
                help us help your customers to reuse, recycle or <br>
                return these Freightmates.
              </span>
            </template>
            <i class="fa fa-info-circle" aria-hidden="true"></i>
          </a-popover>

          <!-- Off Site Trackers Table -->
          <freight-mates-table :columns="offSiteColumns" :data="offSiteTrackers" :loading="isLoadingOffSite" :type="'off-site'"/>
          <br/>
        </a-row>

        <!-- Assets Summary -->
        <a-row>
          <p class="sub-title">Track Your Assets</p>
          <a-popover placement="right">
            <template slot="content">
              <span>
                Know where your assets are for only $2 (+GST) per week. <br>
                Simply attach a FreightMate to your asset, add the asset <br>
                here, then track your asset.
              </span>
            </template>
            <i class="fa fa-info-circle" aria-hidden="true"></i>
          </a-popover>
          <link-track-to-business-modal style="float: right;" @trackerAdded="reloadAssets"/>

          <!-- Assets Table -->
          <freight-mates-table :columns="assetsColumns" :data="assets" :loading="isLoadingAssets" :type="'assets'" @trackerDeleted="reloadAssets"/>
          <br/>
        </a-row>
      </a-col>
    </a-card>
  </div>
</template>

<script>
import FreightMatesTable from "../../components/tables/FreightMates";
import LinkTrackToBusinessModal from "../../components/modals/LinkTrackToBusiness";
import moment from 'moment';

const onSiteColumns = [
  {
    title: 'Freightmate',
    dataIndex: 'id',
    key: 'id',
    scopedSlots: { customRender: 'id' },
    align: 'center',
  },
  {
    title: 'Most Recent Booking',
    dataIndex: 'bookingId',
    key: 'bookingId',
    scopedSlots: { customRender: 'bookingId' },
    align: 'center',
  },
  {
    title: 'Previous Location',
    dataIndex: 'previousLocation',
    key: 'previousLocation',
    scopedSlots: { customRender: 'previousLocation' },
    align: 'center',
  },
  {
    title: 'Last Detected',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    scopedSlots: { customRender: 'updatedAt' },
    align: 'center',
  },
  {
    title: 'Downtime',
    dataIndex: 'unlinkedAt',
    key: 'unlinkedAt',
    scopedSlots: { customRender: 'unlinkedAt' },
    align: 'center',
  },
  {
    title: 'History',
    dataIndex: 'history',
    scopedSlots: { customRender: 'history' },
    align: 'center',
  },
  {
    title: 'Options',
    dataIndex: 'options',
    scopedSlots: { customRender: 'options' },
    align: 'center',
  },
];

const offSiteColumns = [
  {
    title: 'Freightmate',
    dataIndex: 'id',
    key: 'id',
    scopedSlots: { customRender: 'id' },
    align: 'center',
  },
  {
    title: 'Most Recent Booking',
    dataIndex: 'bookingId',
    key: 'bookingId',
    scopedSlots: { customRender: 'bookingId' },
    align: 'center',
  },
  {
    title: 'Current Location',
    dataIndex: 'currentLocation',
    key: 'currentLocation',
    scopedSlots: { customRender: 'currentLocation' },
    align: 'center',
  },
  {
    title: 'Recipient',
    dataIndex: 'recipient',
    key: 'recipient',
    align: 'center',
  },
  {
    title: 'Last Detected',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    scopedSlots: { customRender: 'updatedAt' },
    align: 'center',
  },
  {
    title: 'Downtime',
    dataIndex: 'unlinkedAt',
    key: 'unlinkedAt',
    scopedSlots: { customRender: 'unlinkedAt' },
    align: 'center',
  },
  {
    title: 'History',
    dataIndex: 'history',
    scopedSlots: { customRender: 'history' },
    align: 'center',
  },
  {
    title: 'Options',
    dataIndex: 'options',
    scopedSlots: { customRender: 'options' },
    align: 'center',
  },
];

const assetsColumns = [
  {
    title: 'Freightmate',
    dataIndex: 'id',
    key: 'id',
    scopedSlots: { customRender: 'id' },
    align: 'center',
  },
  {
    title: 'Asset',
    dataIndex: 'assetName',
    key: 'assetName',
    align: 'center',
  },
  {
    title: 'Current Location',
    dataIndex: 'currentLocation',
    key: 'currentLocation',
    scopedSlots: { customRender: 'currentLocation' },
    align: 'center',
  },
  {
    title: 'Last Detected',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    scopedSlots: { customRender: 'updatedAt' },
    align: 'center',
  },
  {
    title: 'Downtime',
    dataIndex: 'unlinkedAt',
    key: 'unlinkedAt',
    scopedSlots: { customRender: 'unlinkedAt' },
    align: 'center',
  },
  {
    title: 'History',
    dataIndex: 'history',
    scopedSlots: { customRender: 'history' },
    align: 'center',
  },
  {
    title: 'Options',
    dataIndex: 'options',
    scopedSlots: { customRender: 'options' },
    align: 'center',
  },
];

export default {
  name: "FreightMateDashboard",
  components: {
    LinkTrackToBusinessModal,
    FreightMatesTable,
  },
  data() {
    return {
      onSiteColumns,
      offSiteColumns,
      assetsColumns,
      isLoadingAssets: false,
      isLoadingOnSite: false,
      isLoadingOffSite: false,
      assets: [],
      onSiteTrackers: [],
      offSiteTrackers: [],
      rawAssets: [],
      rawOnSiteTrackers: [],
      rawOffSiteTrackers: [],
      alertVisible: false,
      alertMessage: '',
      onSiteSummary: '',
      offSiteSummary: '',
    };
  },
  methods: {
    // Fetch assets Freightmates
    loadAssets() {
      this.isLoadingAssets = true;
      axios.get('/send-freight/tracker/business')
          .then(response => {
            console.log(response);
            const data = Object.values(response.data);
            this.rawTrackers = data;
            this.assets = this.mapRawTrackers(data);
          })
          .catch(error => {
            console.log(error.response);
            this.alertVisible = true;
            this.alertMessage = 'Failed to load your assets. ' + error.response.data.message;
          })
          .finally(() => {
            this.isLoadingAssets = false
          });
    },
    // Fetch unlinked on site Freightmates
    loadUnlinkedOnSite() {
      this.isLoadingOnSite = true;
      axios.get('/send-freight/tracker/unlink/onsite')
          .then(response => {
            console.log(response);
            const data = Object.values(response.data);
            this.rawOnSiteTrackers = data;
            this.onSiteTrackers = this.mapRawTrackers(data);
          })
          .catch(error => {
            console.log(error.response);
            this.alertVisible = true;
            this.alertMessage = 'Failed to load your unlinked on site freightmates. ' + error.response.data.message;
          })
          .finally(() => {
            this.isLoadingOnSite = false
          });
    },
    // Fetch unlinked off site Freightmates
    loadUnlinkedOffSite() {
      // todo:
      this.isLoadingOffSite = true;
      axios.get('/send-freight/tracker/unlink/offsite')
          .then(response => {
            console.log(response);
            const data = Object.values(response.data);
            this.rawOffSiteTrackers = data;
            this.offSiteTrackers = this.mapRawTrackers(data);
          })
          .catch(error => {
            console.log(error.response);
            this.alertVisible = true;
            this.alertMessage = 'Failed to load your unlinked off site freightmates. ' + error.response.data.message;
          })
          .finally(() => {
            this.isLoadingOffSite = false
          });
    },
    mapRawTrackers(rawTrackers) {
      console.log(rawTrackers);
      return rawTrackers.map(tracker => {
        return {
          id: tracker.eroad_device_id,
          battery: tracker.battery,
          bookingId: tracker.last_booking_id,
          previousLocation: tracker.previous_location,
          currentLocation: tracker.current_location,
          updatedAt: moment(tracker.last_location.time),
          unlinkedAt: tracker.unlinked_at === '' ? '' : moment(tracker.unlinked_at),
          assetName: tracker.eroad_asset_id,
          recipient: tracker.recipient ? tracker.recipient : ''
        }
      });
    },
    reloadAssets() {
      this.loadAssets();
    }
  },
  mounted() {
    this.loadAssets();
    this.loadUnlinkedOnSite();
    this.loadUnlinkedOffSite();
  },
}
</script>

<style scoped>
.sub-title {
  color: rgb(74, 85, 104);
  font-weight: 500;
  line-height: 26px;
  font-size: 1.2rem;
  display: inline-block;
}
</style>
